/**
 * Telegram Mini App — post-drill school backfill.
 *
 * Opens from the bot's "🎒 Add your school" inline button after a drill
 * completion. Pulls a typeahead list from existing student.school_name
 * values so the student picks from a known set (avoids the typo-variant
 * class that /ops/schools merge tool can't catch — see
 * feedback_normalize_free_text_writes).
 *
 * Writes via the existing PUT /student/profile endpoint. Skip increments
 * the dismiss counter so the bot stops asking.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import TelegramShell from '../components/TelegramShell';
import api from '../services/api';

const COLORS = {
  primary: '#4a7c10',
  primaryDark: '#3a6209',
  border: '#e2e8f0',
  bg: '#f8fafc',
  textPrimary: '#0f172a',
  textMuted: '#64748b',
  pillBg: '#f1f5f9',
  pillBorder: '#cbd5e1',
};

interface SchoolSuggestion {
  name: string;
  student_count: number;
}

function closeMiniApp() {
  try {
    window.Telegram?.WebApp?.close();
  } catch {
    // no-op outside Telegram
  }
}

const TelegramAddSchoolInner: React.FC = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SchoolSuggestion[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Initial load — popular schools.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ suggestions: SchoolSuggestion[] }>(
          '/schools/suggest',
          { params: { q: '', limit: 12 } },
        );
        setSuggestions(res.data.suggestions || []);
      } catch (e: any) {
        // Swallow — typeahead is enhancement; manual entry still works.
      }
    })();
  }, []);

  // Debounced suggestion fetch as the user types.
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(async () => {
      try {
        const res = await api.get<{ suggestions: SchoolSuggestion[] }>(
          '/schools/suggest',
          { params: { q: query, limit: 12 } },
        );
        setSuggestions(res.data.suggestions || []);
      } catch {
        // ignore
      }
    }, 200);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  const submitValue = useMemo(
    () => (selected || query).trim(),
    [selected, query],
  );

  const handleSubmit = async () => {
    const v = submitValue;
    if (v.length < 2) {
      setError('Type or pick a school name');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.put('/student/profile', { school_name: v });
      setSubmitted(true);
      window.setTimeout(closeMiniApp, 900);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save');
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await api.post('/me/school/dismiss');
    } catch {
      // best-effort; bot caps regardless
    }
    closeMiniApp();
  };

  if (submitted) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: COLORS.textPrimary }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>School saved.</div>
        <div style={{ marginTop: 6, color: COLORS.textMuted, fontSize: 14 }}>
          You'll see {submitValue} on the school leaderboard.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 16,
        maxWidth: 480,
        margin: '0 auto',
        minHeight: '100vh',
        background: COLORS.bg,
      }}
    >
      <h1 style={{ margin: 0, fontSize: 20, color: COLORS.textPrimary }}>
        Add your school
      </h1>
      <p style={{ marginTop: 6, color: COLORS.textMuted, fontSize: 14 }}>
        Your school could be on the leaderboard. Pick from below or type it in.
      </p>

      <input
        type="text"
        autoFocus
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSelected(null);
          setError(null);
        }}
        placeholder="Type your school"
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 16,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 10,
          marginTop: 12,
          boxSizing: 'border-box',
        }}
      />

      {error && (
        <div style={{ color: '#dc2626', marginTop: 8, fontSize: 13 }}>{error}</div>
      )}

      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {suggestions.map((s) => {
          const isSelected = selected === s.name;
          return (
            <button
              key={s.name}
              type="button"
              onClick={() => {
                setSelected(s.name);
                setQuery(s.name);
                setError(null);
              }}
              style={{
                background: isSelected ? COLORS.primary : COLORS.pillBg,
                color: isSelected ? '#fff' : COLORS.textPrimary,
                border: `1px solid ${isSelected ? COLORS.primary : COLORS.pillBorder}`,
                borderRadius: 999,
                padding: '6px 12px',
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {s.name}
              {s.student_count > 1 && (
                <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 11 }}>
                  · {s.student_count}
                </span>
              )}
            </button>
          );
        })}
        {suggestions.length === 0 && query.length >= 2 && (
          <span style={{ color: COLORS.textMuted, fontSize: 13 }}>
            No matches — type your school name and tap Save.
          </span>
        )}
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button
          type="button"
          onClick={handleSkip}
          disabled={submitting}
          style={{
            flex: 1,
            padding: '10px 14px',
            background: 'transparent',
            color: COLORS.textMuted,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || submitValue.length < 2}
          style={{
            flex: 2,
            padding: '10px 14px',
            background:
              submitValue.length < 2 ? COLORS.pillBg : COLORS.primary,
            color: submitValue.length < 2 ? COLORS.textMuted : '#fff',
            border: 'none',
            borderRadius: 10,
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const TelegramAddSchool: React.FC = () => (
  <TelegramShell>
    <TelegramAddSchoolInner />
  </TelegramShell>
);

export default TelegramAddSchool;
